// Clone of CampaignsPage. Same form, same hacker loading screen. The scrape
// step writes deterministic mock leads into in-memory demo state instead of
// calling the cloud function.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Globe, Users, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { HackerLoadingScreen } from '@/components/ui/HackerLoadingScreen';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatDate } from '@/lib/utils';
import { useDemoState } from './DemoStateProvider';
import { generateMockLeads } from './mockLeadsData';

const COUNTRIES = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
];

const NICHES = [
  { value: 'any', label: 'Any Niche (Random)' },
  { value: 'plumber', label: 'Plumber' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'hvac', label: 'HVAC / Air Conditioning' },
  { value: 'roofing', label: 'Roofing Contractor' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning', label: 'Cleaning Service' },
  { value: 'painting', label: 'Painter' },
  { value: 'flooring', label: 'Flooring Contractor' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'locksmith', label: 'Locksmith' },
  { value: 'garage_door', label: 'Garage Door Repair' },
  { value: 'pool_service', label: 'Pool Service' },
  { value: 'tree_service', label: 'Tree Service' },
  { value: 'moving', label: 'Moving Company' },
  { value: 'auto_repair', label: 'Auto Repair Shop' },
  { value: 'dentist', label: 'Dentist' },
  { value: 'chiropractor', label: 'Chiropractor' },
  { value: 'veterinarian', label: 'Veterinarian' },
  { value: 'salon', label: 'Hair Salon / Barber' },
  { value: 'spa', label: 'Spa / Massage' },
  { value: 'gym', label: 'Gym / Fitness Center' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'photography', label: 'Photographer' },
  { value: 'real_estate', label: 'Real Estate Agent' },
  { value: 'accounting', label: 'Accountant / CPA' },
  { value: 'lawyer', label: 'Lawyer / Attorney' },
  { value: 'insurance', label: 'Insurance Agent' },
  { value: 'daycare', label: 'Daycare / Childcare' },
  { value: 'tutoring', label: 'Tutoring Service' },
  { value: 'pet_grooming', label: 'Pet Grooming' },
  { value: 'catering', label: 'Catering' },
  { value: 'florist', label: 'Florist' },
  { value: 'tailor', label: 'Tailor / Alterations' },
];

const MAX_LEADS_OPTIONS: Array<{ value: number | 'custom'; label: string }> = [
  { value: 10, label: '10 leads' },
  { value: 25, label: '25 leads' },
  { value: 50, label: '50 leads' },
  { value: 100, label: '100 leads' },
  { value: 'custom', label: 'Custom amount' },
];

const US_CITIES = [
  'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ',
  'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA',
  'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC',
  'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC',
  'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Oklahoma City, OK',
  'Portland, OR', 'Las Vegas, NV', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD',
  'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Fresno, CA', 'Mesa, AZ',
  'Sacramento, CA', 'Atlanta, GA', 'Kansas City, MO', 'Colorado Springs, CO', 'Miami, FL',
  'Raleigh, NC', 'Omaha, NE', 'Long Beach, CA', 'Virginia Beach, VA', 'Oakland, CA',
  'Minneapolis, MN', 'Tulsa, OK', 'Tampa, FL', 'Arlington, TX', 'New Orleans, LA',
];

export function DemoCampaigns() {
  const navigate = useNavigate();
  const { campaigns, addCampaign, deleteCampaign, scrapeCampaign } = useDemoState();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name: '', niche: 'any', country: 'US', maxLeads: 25 });
  const [isCustomLeads, setIsCustomLeads] = useState(false);
  const [customLeadsValue, setCustomLeadsValue] = useState('');
  const [isLocalCampaign, setIsLocalCampaign] = useState(false);
  const [localCity, setLocalCity] = useState('');
  const [citySuggestions, setCitySuggestions] = useState<Array<{ name: string; fullName: string }>>([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [scrapingId, setScrapingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showHackerScreen, setShowHackerScreen] = useState(false);
  const [scrapingLogs, setScrapingLogs] = useState<string[]>([]);
  const [scrapingStats, setScrapingStats] = useState({
    businessesFound: 0,
    businessesWithEmail: 0,
    businessesNoEmail: 0,
    citiesSearched: 0,
    progress: 0,
  });
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [foundLeads, setFoundLeads] = useState<Array<{
    name: string;
    rating?: number;
    reviewCount?: number;
    location?: string;
    hasEmail?: boolean;
    hasPhone?: boolean;
    imageUrl?: string;
  }>>([]);

  const handleCitySearch = (query: string) => {
    setLocalCity(query);
    if (query.length >= 2) {
      const filtered = US_CITIES
        .filter((city) => city.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8)
        .map((city) => ({ name: city.split(',')[0], fullName: city }));
      setCitySuggestions(filtered);
      setShowCitySuggestions(filtered.length > 0);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const selectCity = (city: { name: string; fullName: string }) => {
    setLocalCity(city.fullName);
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };

  const handleCreate = () => {
    if (!newCampaign.name.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (isLocalCampaign && !localCity.trim()) {
      toast.error('Please enter a city for your local campaign');
      return;
    }
    const nicheLabel = NICHES.find((n) => n.value === newCampaign.niche)?.label || newCampaign.niche;
    const location = isLocalCampaign ? localCity.trim() : COUNTRIES.find((c) => c.code === newCampaign.country)?.name || newCampaign.country;

    addCampaign({
      userId: 'demo-user',
      name: newCampaign.name.trim(),
      niche: newCampaign.niche,
      nicheLabel,
      location,
      isLocalCampaign,
      status: 'active',
      maxLeads: newCampaign.maxLeads,
    });

    toast.success('Campaign created successfully');
    setIsCreateOpen(false);
    setNewCampaign({ name: '', niche: 'any', country: 'US', maxLeads: 25 });
    setIsCustomLeads(false);
    setCustomLeadsValue('');
    setIsLocalCampaign(false);
    setLocalCity('');
  };

  const handleScrape = async (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) return;

    setScrapingId(campaignId);
    setShowHackerScreen(true);
    setScrapingLogs([]);
    setFoundLeads([]);
    setScrapingStats({ businessesFound: 0, businessesWithEmail: 0, businessesNoEmail: 0, citiesSearched: 0, progress: 0 });
    setCurrentQuery(campaign.nicheLabel || campaign.niche);
    setCurrentLocation(campaign.location);

    // Drip-feed leads into the loading screen for visual realism.
    const allMockLeads = generateMockLeads(campaign);
    const totalLeads = allMockLeads.length;
    const dripCount = Math.min(totalLeads, 30); // animate the first 30 max
    const dripDelay = 80; // ms between drops

    for (let i = 0; i < dripCount; i++) {
      await new Promise((r) => setTimeout(r, dripDelay));
      const lead = allMockLeads[i];
      setFoundLeads((prev) => [
        ...prev,
        {
          name: lead.businessName,
          rating: lead.rating,
          reviewCount: lead.reviewCount,
          location: lead.location,
          hasEmail: !!lead.email,
          hasPhone: !!lead.phone,
          imageUrl: lead.businessImages[0],
        },
      ]);
      setScrapingStats((prev) => ({
        ...prev,
        businessesFound: i + 1,
        businessesWithEmail: prev.businessesWithEmail + (lead.email ? 1 : 0),
        progress: Math.min(85, ((i + 1) / dripCount) * 85),
      }));
    }

    // Commit all leads to state and finalize the loading screen.
    await scrapeCampaign(campaignId);
    setScrapingStats((prev) => ({
      ...prev,
      businessesFound: totalLeads,
      businessesWithEmail: allMockLeads.filter((l) => l.email).length,
      progress: 100,
    }));
    setScrapingLogs([
      `✓ Searched for "${campaign.nicheLabel}" in ${campaign.location}`,
      `✓ Found ${totalLeads} businesses`,
      `✓ ${allMockLeads.filter((l) => l.email).length} have valid contact info`,
      `✓ Scraping complete! Redirecting...`,
    ]);

    await new Promise((r) => setTimeout(r, 1500));
    setShowHackerScreen(false);
    setScrapingId(null);
    toast.success(`Found ${totalLeads} businesses! Redirecting to Leads...`);
    navigate('/demo/leads');
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmId) return;
    deleteCampaign(deleteConfirmId);
    toast.success('Campaign deleted');
    setDeleteConfirmId(null);
  };

  const getCountryDisplay = (location: string) => {
    const country = COUNTRIES.find((c) => c.code === location || c.name === location);
    return country ? `${country.flag} ${country.name}` : location;
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Campaigns</h1>
          <p className="text-gray-400 mt-1">Manage your lead scraping campaigns</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {campaigns.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <GlassCard key={campaign.id} hover>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{campaign.name}</h3>
                  <p className="text-sm text-gray-500">
                    {campaign.nicheLabel || NICHES.find((n) => n.value === campaign.niche)?.label || campaign.niche}
                  </p>
                </div>
                <Badge variant={campaign.status === 'active' ? 'success' : 'secondary'}>
                  {campaign.status}
                </Badge>
              </div>

              <div className="space-y-2 text-sm text-gray-400 mb-6">
                <p className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {getCountryDisplay(campaign.location)}
                </p>
                <p className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Max {campaign.maxLeads || 25} leads per scrape
                </p>
                {campaign.lastScrapedAt && (
                  <p>Last scraped: {formatDate(campaign.lastScrapedAt)}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {campaign.lastScrapedAt ? (
                  <div className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Scraped
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    loading={scrapingId === campaign.id}
                    onClick={() => handleScrape(campaign.id)}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Scrape Leads
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => setDeleteConfirmId(campaign.id)}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No campaigns yet</h3>
          <p className="text-gray-400 mb-6">Create your first campaign to start finding leads</p>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </GlassCard>
      )}

      <Dialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) {
            setIsCustomLeads(false);
            setCustomLeadsValue('');
          }
        }}
      >
        <DialogContent className="top-[6%] translate-y-0">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription>Set up a new campaign to scrape leads from Google Places</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Campaign Name</Label>
              <Input
                id="name"
                placeholder="e.g., US Plumbers Campaign"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="niche">Business Niche</Label>
              <Select
                value={newCampaign.niche}
                onValueChange={(value) => setNewCampaign({ ...newCampaign, niche: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NICHES.map((niche) => (
                    <SelectItem key={niche.value} value={niche.value}>{niche.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Select "Any Niche" for random businesses without websites</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="country">Target Location</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-400">Local Campaign</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isLocalCampaign}
                    onClick={() => {
                      setIsLocalCampaign(!isLocalCampaign);
                      if (!isLocalCampaign) setLocalCity('');
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      isLocalCampaign ? 'bg-primary-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        isLocalCampaign ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>

              {isLocalCampaign ? (
                <div className="relative">
                  <Input
                    placeholder="Start typing a city (e.g., Jacksonville, FL)"
                    value={localCity}
                    onChange={(e) => handleCitySearch(e.target.value)}
                    onFocus={() => localCity.length >= 2 && citySuggestions.length > 0 && setShowCitySuggestions(true)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-[#0a0a0a] border border-rose/20 rounded-lg shadow-xl overflow-hidden">
                      {citySuggestions.map((city, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full px-4 py-2 text-left text-sm text-white hover:bg-primary-500/20 transition-colors"
                          onMouseDown={() => selectCity(city)}
                        >
                          📍 {city.fullName}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Search for specific cities within the US</p>
                </div>
              ) : (
                <Select
                  value={newCampaign.country}
                  onValueChange={(value) => setNewCampaign({ ...newCampaign, country: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxLeads">Maximum Leads per Scrape</Label>
              {isCustomLeads ? (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    placeholder="Enter amount (1-500)"
                    value={customLeadsValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomLeadsValue(val);
                      const num = parseInt(val);
                      if (!isNaN(num) && num >= 1 && num <= 500) {
                        setNewCampaign({ ...newCampaign, maxLeads: num });
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsCustomLeads(false);
                      setCustomLeadsValue('');
                      setNewCampaign({ ...newCampaign, maxLeads: 25 });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Select
                  value={newCampaign.maxLeads.toString()}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setIsCustomLeads(true);
                      setCustomLeadsValue('');
                    } else {
                      setNewCampaign({ ...newCampaign, maxLeads: parseInt(value) });
                    }
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MAX_LEADS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value.toString()}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HackerLoadingScreen
        isOpen={showHackerScreen}
        title="SCRAPING LEADS"
        subtitle="Finding businesses without websites & enriching contact info"
        mode="scraping"
        logs={scrapingLogs}
        stats={scrapingStats}
        currentQuery={currentQuery}
        currentLocation={currentLocation}
        foundLeads={foundLeads}
      />

      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this campaign? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
